import { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import initApi from '@/api/initApiV2';

import {
  PolicyTemplatesApi,
  type PolicyTemplate,
  type PolicyTemplateOverviewPaginated,
  type PolicyTemplatePaginated,
  type PolicyTemplateVersion,
  type PolicyTemplateVersionPaginated,
  type PolicyTemplatesApiCreatePolicyTemplateRequest,
  type PolicyTemplatesApiCreatePolicyTemplateVersionRequest,
  type PolicyTemplatesApiGetPolicyTemplateRequest,
  type PolicyTemplatesApiGetPolicyTemplateVersionRequest,
  type PolicyTemplatesApiQueryPolicyTemplateVersionsRequest,
  type PolicyTemplatesApiQueryPolicyTemplatesExpandedRequest,
  type PolicyTemplatesApiQueryPolicyTemplatesRequest,
  type PolicyTemplatesApiUpdatePolicyTemplateStatusRequest,
  type PolicyTemplatesApiUpdatePolicyTemplateVersionRequest,
  type PolicyTemplatesApiUpdatePolicyTemplateVersionStatusRequest,
  type PolicyTemplatesApiUpdatePlatformFrameworksRequest,
  type PolicyTemplatesApiGetPolicyTemplateVersionFrameworksRequest,
} from '@/clients/v2';

import { handleApiError } from '@/api/handleApiError.ts';
import type { RequestConfig } from '@/api/Request';

export const useTemplatesStore = defineStore('templates-store-v2', () => {
  const templatesData = ref<PolicyTemplateOverviewPaginated | null>(null);
  const templatesPlainData = ref<PolicyTemplatePaginated | null>(null);
  const currentTemplate = ref<PolicyTemplate | null>(null);
  const currentTemplateVersion = ref<PolicyTemplateVersion | null>(null);
  const templateVersionsData = ref<PolicyTemplateVersionPaginated | null>(null);

  const queryTemplates = async (
    params: PolicyTemplatesApiQueryPolicyTemplatesExpandedRequest = {},
    options?: RequestConfig,
  ) => {
    const api = await initApi(PolicyTemplatesApi);

    try {
      const response = await api?.queryPolicyTemplatesExpanded(params, options);

      if (!response) return;

      templatesData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const queryTemplatesPlain = async (
    params: PolicyTemplatesApiQueryPolicyTemplatesRequest = {},
    options?: RequestConfig,
  ) => {
    const api = await initApi(PolicyTemplatesApi);

    try {
      const response = await api?.queryPolicyTemplates(params, options);

      if (!response) return;

      templatesPlainData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getTemplate = async (
    params: PolicyTemplatesApiGetPolicyTemplateRequest,
    options?: RequestConfig,
  ) => {
    const api = await initApi(PolicyTemplatesApi);

    try {
      const response = await api?.getPolicyTemplate(params, options);

      if (!response) return;

      currentTemplate.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createTemplate = async (
    params: PolicyTemplatesApiCreatePolicyTemplateRequest,
    options?: RequestConfig,
  ) => {
    const api = await initApi(PolicyTemplatesApi);

    try {
      const response = await api?.createPolicyTemplate(params, options);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getTemplateVersion = async (
    params: PolicyTemplatesApiGetPolicyTemplateVersionRequest,
    options?: RequestConfig,
  ) => {
    const api = await initApi(PolicyTemplatesApi);

    try {
      const response = await api?.getPolicyTemplateVersion(params, options);

      if (!response) return;

      currentTemplateVersion.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const queryTemplateVersions = async (
    params: PolicyTemplatesApiQueryPolicyTemplateVersionsRequest,
    options?: RequestConfig,
  ) => {
    const api = await initApi(PolicyTemplatesApi);

    try {
      const response = await api?.queryPolicyTemplateVersions(params, options);

      if (!response) return;

      templateVersionsData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateTemplateVersion = async (
    params: PolicyTemplatesApiUpdatePolicyTemplateVersionRequest,
    options?: RequestConfig,
  ) => {
    const api = await initApi(PolicyTemplatesApi);

    try {
      const response = await api?.updatePolicyTemplateVersion(params, options);

      if (!response) return;

      currentTemplateVersion.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateTemplateVersionStatus = async (
    params: PolicyTemplatesApiUpdatePolicyTemplateVersionStatusRequest,
    options?: RequestConfig,
  ) => {
    const api = await initApi(PolicyTemplatesApi);

    try {
      const response = await api?.updatePolicyTemplateVersionStatus(params, options);

      if (!response) return;

      currentTemplateVersion.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateTemplateStatus = async (
    params: PolicyTemplatesApiUpdatePolicyTemplateStatusRequest,
    options?: RequestConfig,
  ) => {
    const api = await initApi(PolicyTemplatesApi);

    try {
      const response = await api?.updatePolicyTemplateStatus(params, options);

      if (!response) return;

      currentTemplate.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createTemplateVersion = async (
    params: PolicyTemplatesApiCreatePolicyTemplateVersionRequest,
    options?: RequestConfig,
  ) => {
    const api = await initApi(PolicyTemplatesApi);

    try {
      const response = await api?.createPolicyTemplateVersion(params, options);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updateTemplateFrameworks = async (
    params: PolicyTemplatesApiUpdatePlatformFrameworksRequest,
    options?: RequestConfig,
  ) => {
    const api = await initApi(PolicyTemplatesApi);

    try {
      const response = await api?.updatePlatformFrameworks(params, options);

      if (!response) return;

      currentTemplateVersion.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getTemplateVersionFrameworks = async (
    params: PolicyTemplatesApiGetPolicyTemplateVersionFrameworksRequest,
    options?: RequestConfig,
  ) => {
    const api = await initApi(PolicyTemplatesApi);

    try {
      const response = await api?.getPolicyTemplateVersionFrameworks(params, options);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const resetCurrentTemplate = () => {
    currentTemplate.value = null;
    templateVersionsData.value = null;
  };

  const resetCurrentTemplateVersion = () => {
    currentTemplateVersion.value = null;
  };

  return {
    templatesData,
    templatesPlainData,
    currentTemplate,
    currentTemplateVersion,
    templateVersionsData,

    queryTemplates,
    queryTemplatesPlain,
    getTemplate,
    createTemplate,
    getTemplateVersion,
    queryTemplateVersions,
    updateTemplateVersion,
    updateTemplateVersionStatus,
    updateTemplateStatus,
    createTemplateVersion,
    updateTemplateFrameworks,
    getTemplateVersionFrameworks,
    resetCurrentTemplate,
    resetCurrentTemplateVersion,
  };
});
